/**
 * Shareable simulator scenario via ?scenario=<encodeURIComponent(JSON)>
 * Readable JSON keys for hand-editing / AI-assisted URL construction.
 *
 * v1 example (values illustrative):
 * {
 *   "v": 1,
 *   "weatherYear": 2013,
 *   "supplyWeatherYear": 2019,
 *   "demandTwh": 500,
 *   "solarGw": 10, "windOnGw": 20, "windOffGw": 5, "nuclearGw": 5, "hydroGw": 3,
 *   "coalGw": 0, "gasGw": 10, "oilGw": 0, "bioGw": 1, "geoGw": 0,
 *   "batteryStorageGw": 2, "batteryDurationHours": 4,
 *   "pumpedStorageGw": 1, "pumpedDurationHours": 6
 * }
 * Omit optional "demandTwh" to use the default demand from the profile API after load.
 * Country is always taken from the path (/country/DEU/simulator). Capacity data year is fixed in-app (2022).
 */

import { SIMULATOR_GW_KEYS } from './simulatorRawTechMapping';

export const SIMULATOR_SCENARIO_QUERY_KEY = 'scenario';
export const SIMULATOR_SCENARIO_VERSION = 1;

/** Shared top-level keys (not inside raw `capacity`). */
export const SHARED_SCENARIO_KEYS = [
  'demandTwh',
  'weatherYear',
  'supplyWeatherYear',
  'batteryDurationHours',
  'pumpedDurationHours'
];

/** Supply weather years allowed by GenerationProfileChart (must stay in sync). */
export const SIMULATOR_SUPPLY_WEATHER_YEARS = [2010, 2013, 2015, 2019, 2021];

/**
 * True when the share payload includes an explicit demand (TWh) the user should keep
 * after /generation-profile returns (chartData must not overwrite the input).
 */
export function scenarioUrlHasExplicitDemandTwh(parsed) {
  if (!parsed || typeof parsed !== 'object') return false;
  if (parsed.v !== SIMULATOR_SCENARIO_VERSION) return false;
  const d = parsed.demandTwh;
  if (d === undefined || d === null || d === '') return false;
  const n = typeof d === 'number' ? d : parseFloat(d);
  return Number.isFinite(n) && n > 0 && n <= 99999;
}

/**
 * @param {string} [search]
 * @returns {object|null} Parsed scenario object or null if missing/invalid.
 */
export function parseSimulatorScenarioFromSearch(search) {
  if (!search || typeof search !== 'string') return null;
  const qs = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(qs);
  const raw = params.get(SIMULATOR_SCENARIO_QUERY_KEY);
  if (!raw || !raw.trim()) return null;
  let parsed;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  if (parsed.v !== SIMULATOR_SCENARIO_VERSION) return null;
  return parsed;
}

function finiteNum(v, fallback = 0) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Merge optional ?scenario= fields onto DB capacity snapshot (partial URLs keep DB for omitted keys).
 * @param {object} raw — parseSimulatorScenarioFromSearch
 * @param {object} db — numeric GW / years from capacity API + current weather refs
 */
export function buildMergedScenarioInput(raw, db) {
  const pick = (key, fallback) => {
    if (raw == null || typeof raw !== 'object') return fallback;
    const v = raw[key];
    if (v === undefined || v === null || v === '') return fallback;
    return v;
  };
  return {
    v: SIMULATOR_SCENARIO_VERSION,
    weatherYear: pick('weatherYear', db.weatherYear ?? 2013),
    supplyWeatherYear: pick('supplyWeatherYear', db.supplyWeatherYear ?? 2013),
    demandTwh: pick('demandTwh', undefined),
    solarGw: pick('solarGw', db.solarGw ?? 0),
    windOnGw: pick('windOnGw', db.windOnGw ?? 0),
    windOffGw: pick('windOffGw', db.windOffGw ?? 0),
    nuclearGw: pick('nuclearGw', db.nuclearGw ?? 0),
    hydroGw: pick('hydroGw', db.hydroGw ?? 0),
    coalGw: pick('coalGw', db.coalGw ?? 0),
    gasGw: pick('gasGw', db.gasGw ?? 0),
    oilGw: pick('oilGw', db.oilGw ?? 0),
    bioGw: pick('bioGw', db.bioGw ?? 0),
    geoGw: pick('geoGw', db.geoGw ?? 0),
    batteryStorageGw: pick('batteryStorageGw', db.batteryStorageGw ?? 0),
    batteryDurationHours: pick('batteryDurationHours', db.batteryDurationHours ?? 4),
    pumpedStorageGw: pick('pumpedStorageGw', db.pumpedStorageGw ?? 0),
    pumpedDurationHours: pick('pumpedDurationHours', db.pumpedDurationHours ?? 6)
  };
}

/**
 * Clamp GW to a positive limit when limit is finite and > 0.
 * @param {number} gw
 * @param {number|null|undefined} limitGw
 */
export function clampSimulatorGwToLimit(gw, limitGw) {
  const g = Math.max(0, finiteNum(gw, 0));
  if (limitGw != null && Number.isFinite(limitGw) && limitGw > 0) {
    return Math.min(g, limitGw);
  }
  return g;
}

/**
 * Normalize parsed scenario: clamp renewables, coerce supply weather year, etc.
 * @param {object} raw — output of parseSimulatorScenarioFromSearch
 * @param {{ solar: number|null, windOn: number|null, windOff: number|null }} limits
 * @param {number[]} availableDemandWeatherYears — empty = skip demand-year validation
 */
export function normalizeSimulatorScenario(raw, limits, availableDemandWeatherYears = []) {
  if (!raw || typeof raw !== 'object') return null;

  let supplyWeatherYear = finiteNum(raw.supplyWeatherYear, 2013);
  if (!SIMULATOR_SUPPLY_WEATHER_YEARS.includes(supplyWeatherYear)) {
    supplyWeatherYear = SIMULATOR_SUPPLY_WEATHER_YEARS.includes(2013)
      ? 2013
      : SIMULATOR_SUPPLY_WEATHER_YEARS[0];
  }

  let weatherYear = finiteNum(raw.weatherYear, 2013);
  if (availableDemandWeatherYears.length > 0 && !availableDemandWeatherYears.includes(weatherYear)) {
    weatherYear = availableDemandWeatherYears.includes(2013)
      ? 2013
      : availableDemandWeatherYears[0];
  }

  const demandTwh =
    raw.demandTwh === undefined || raw.demandTwh === null || raw.demandTwh === ''
      ? undefined
      : finiteNum(raw.demandTwh, NaN);
  const demandTwhOut =
    Number.isFinite(demandTwh) && demandTwh > 0 && demandTwh <= 99999 ? demandTwh : undefined;

  const solarGw = clampSimulatorGwToLimit(raw.solarGw, limits?.solar);
  const windOnGw = clampSimulatorGwToLimit(raw.windOnGw, limits?.windOn);
  const windOffGw = clampSimulatorGwToLimit(raw.windOffGw, limits?.windOff);

  return {
    v: SIMULATOR_SCENARIO_VERSION,
    weatherYear,
    supplyWeatherYear,
    demandTwh: demandTwhOut,
    solarGw,
    windOnGw,
    windOffGw,
    nuclearGw: Math.max(0, finiteNum(raw.nuclearGw, 0)),
    hydroGw: Math.max(0, finiteNum(raw.hydroGw, 0)),
    coalGw: Math.max(0, finiteNum(raw.coalGw, 0)),
    gasGw: Math.max(0, finiteNum(raw.gasGw, 0)),
    oilGw: Math.max(0, finiteNum(raw.oilGw, 0)),
    bioGw: Math.max(0, finiteNum(raw.bioGw, 0)),
    geoGw: Math.max(0, finiteNum(raw.geoGw, 0)),
    batteryStorageGw: Math.max(0, finiteNum(raw.batteryStorageGw, 0)),
    batteryDurationHours: Math.max(0, finiteNum(raw.batteryDurationHours, 4)),
    pumpedStorageGw: Math.max(0, finiteNum(raw.pumpedStorageGw, 0)),
    pumpedDurationHours: Math.max(0, finiteNum(raw.pumpedDurationHours, 6))
  };
}

/**
 * Build share payload from current simulator fields (v1 readable JSON).
 */
export function buildSimulatorScenarioV1(fields) {
  const {
    weatherYear,
    supplyWeatherYear,
    demandTwhInput,
    solarInputGW,
    windInputGW,
    windOffInputGW,
    nuclearInputGW,
    hydroInputGW,
    coalInputGW,
    gasInputGW,
    oilInputGW,
    bioInputGW,
    geoInputGW,
    storageInputGW,
    storageDurationH,
    pumpedStorageInputGW,
    pumpedStorageDurationH
  } = fields;

  const payload = {
    v: SIMULATOR_SCENARIO_VERSION,
    weatherYear: finiteNum(weatherYear, 2013),
    supplyWeatherYear: finiteNum(supplyWeatherYear, 2013),
    solarGw: Math.max(0, finiteNum(solarInputGW, 0)),
    windOnGw: Math.max(0, finiteNum(windInputGW, 0)),
    windOffGw: Math.max(0, finiteNum(windOffInputGW, 0)),
    nuclearGw: Math.max(0, finiteNum(nuclearInputGW, 0)),
    hydroGw: Math.max(0, finiteNum(hydroInputGW, 0)),
    coalGw: Math.max(0, finiteNum(coalInputGW, 0)),
    gasGw: Math.max(0, finiteNum(gasInputGW, 0)),
    oilGw: Math.max(0, finiteNum(oilInputGW, 0)),
    bioGw: Math.max(0, finiteNum(bioInputGW, 0)),
    geoGw: Math.max(0, finiteNum(geoInputGW, 0)),
    batteryStorageGw: Math.max(0, finiteNum(storageInputGW, 0)),
    batteryDurationHours: Math.max(0, finiteNum(storageDurationH, 4)),
    pumpedStorageGw: Math.max(0, finiteNum(pumpedStorageInputGW, 0)),
    pumpedDurationHours: Math.max(0, finiteNum(pumpedStorageDurationH, 6))
  };

  const d = parseFloat(String(demandTwhInput ?? '').trim());
  if (Number.isFinite(d) && d > 0) {
    payload.demandTwh = d;
  }

  return payload;
}

export function encodeSimulatorScenarioSearchParam(payload) {
  return encodeURIComponent(JSON.stringify(payload));
}

export function pickInitialDemandWeatherYearFromScenario(y) {
  return Number.isFinite(y) ? y : 2013;
}

export function pickInitialSupplyWeatherYearFromScenario(y) {
  const n = Number.isFinite(y) ? y : 2013;
  if (SIMULATOR_SUPPLY_WEATHER_YEARS.includes(n)) return n;
  return SIMULATOR_SUPPLY_WEATHER_YEARS.includes(2013) ? 2013 : SIMULATOR_SUPPLY_WEATHER_YEARS[0];
}

/**
 * Keys explicitly set by a native ?scenario= JSON (present in payload).
 * @returns {Set<string>}
 */
export function buildUrlSpecifiedKeysFromNative(parsed) {
  const keys = new Set();
  if (!parsed || parsed.v !== SIMULATOR_SCENARIO_VERSION) return keys;
  for (const k of [...SIMULATOR_GW_KEYS, ...SHARED_SCENARIO_KEYS]) {
    const v = parsed[k];
    if (v !== undefined && v !== null && v !== '') {
      keys.add(k);
    }
  }
  return keys;
}

/**
 * Keys from a raw URL: mapped capacity + shared fields present in JSON.
 * Fossil keys zero-defaulted without URL entry are excluded.
 * @param {object} rawScenario
 * @param {Record<string, number>} mappedGws — from mapRawCapacityToSimulatorGws (before fossil zero inject)
 * @returns {Set<string>}
 */
export function buildUrlSpecifiedKeysFromRaw(rawScenario, mappedGws) {
  const keys = new Set();
  for (const k of SIMULATOR_GW_KEYS) {
    if (mappedGws[k] !== undefined) {
      keys.add(k);
    }
  }
  for (const k of SHARED_SCENARIO_KEYS) {
    const v = rawScenario?.[k];
    if (v !== undefined && v !== null && v !== '') {
      keys.add(k);
    }
  }
  return keys;
}
